import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  useContentAssignments,
  useProgramsWithChapters,
  useAddContentAssignment,
  useUpdateContentAssignment,
  useRemoveContentAssignment,
  type ContentAssignment,
} from '@/hooks/useContentChapterAssignments';

interface Props {
  contentId: string;
}

export function ContentChapterAssignmentsTab({ contentId }: Props) {
  const { data: assignments = [], isLoading } = useContentAssignments(contentId);
  const { data: programs = [] } = useProgramsWithChapters();
  const addMut = useAddContentAssignment(contentId);
  const updateMut = useUpdateContentAssignment(contentId);
  const removeMut = useRemoveContentAssignment(contentId);

  const [programId, setProgramId] = useState<string>('');
  const [chapterId, setChapterId] = useState<string>('');
  const [mandatory, setMandatory] = useState(true);
  const [order, setOrder] = useState<string>('');
  const [removing, setRemoving] = useState<ContentAssignment | null>(null);

  const chapters = useMemo(
    () => programs.find((p) => p.id === programId)?.chapters ?? [],
    [programs, programId]
  );

  const handleAdd = async () => {
    if (!chapterId) {
      toast.error('Selecciona un capítulo');
      return;
    }
    try {
      await addMut.mutateAsync({
        chapterId,
        isMandatory: mandatory,
        sortOrder: order === '' ? null : Number(order),
      });
      toast.success('Contenido asignado al capítulo');
      setChapterId('');
      setOrder('');
    } catch (e: any) {
      toast.error(e?.message ?? 'Error al asignar');
    }
  };

  const handleUpdate = async (
    id: string,
    updates: { is_mandatory?: boolean; sort_order?: number }
  ) => {
    try {
      await updateMut.mutateAsync({ id, ...updates });
    } catch (e: any) {
      toast.error(e?.message ?? 'Error al actualizar');
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    try {
      await removeMut.mutateAsync(removing.id);
      toast.success('Asignación eliminada');
      setRemoving(null);
    } catch (e: any) {
      toast.error(e?.message ?? 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Alta */}
      <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2">
            <Label>Ruta</Label>
            <Select
              value={programId}
              onValueChange={(v) => {
                setProgramId(v);
                setChapterId('');
              }}
            >
              <SelectTrigger><SelectValue placeholder="Selecciona una ruta" /></SelectTrigger>
              <SelectContent>
                {programs.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Capítulo</Label>
            <Select value={chapterId} onValueChange={setChapterId} disabled={!programId}>
              <SelectTrigger>
                <SelectValue placeholder={programId ? 'Selecciona un capítulo' : 'Elige primero la ruta'} />
              </SelectTrigger>
              <SelectContent>
                {chapters.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-6">
          <div className="flex items-center gap-2">
            <Switch checked={mandatory} onCheckedChange={setMandatory} id="new-mandatory" />
            <Label htmlFor="new-mandatory">Obligatorio</Label>
          </div>
          <div className="grid gap-2">
            <Label>Orden (opcional)</Label>
            <Input
              type="number"
              className="w-32"
              placeholder="Al final"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            />
          </div>
          <Button onClick={handleAdd} disabled={addMut.isPending}>
            <Plus className="h-4 w-4 mr-1.5" /> Asignar a capítulo
          </Button>
        </div>
      </div>

      {/* Asignaciones actuales */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ruta</TableHead>
              <TableHead>Capítulo</TableHead>
              <TableHead className="w-[120px]">Obligatorio</TableHead>
              <TableHead className="w-[160px]">Orden</TableHead>
              <TableHead className="text-right w-[80px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Cargando...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && assignments.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Este contenido aún no está asignado a ninguna ruta.
                </TableCell>
              </TableRow>
            )}
            {assignments.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <div className="font-medium">{a.program_name}</div>
                  {a.program_status && (
                    <Badge variant="outline" className="mt-1 text-xs">{a.program_status}</Badge>
                  )}
                </TableCell>
                <TableCell>{a.chapter_name}</TableCell>
                <TableCell>
                  <Switch
                    checked={a.is_mandatory}
                    onCheckedChange={(v) => handleUpdate(a.id, { is_mandatory: v })}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleUpdate(a.id, { sort_order: Math.max(0, a.sort_order - 1) })}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Input
                      type="number"
                      className="w-16 h-8"
                      value={a.sort_order}
                      onChange={(e) => handleUpdate(a.id, { sort_order: Number(e.target.value) })}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleUpdate(a.id, { sort_order: a.sort_order + 1 })}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => setRemoving(a)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el contenido de este capítulo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se quitará de <strong>{removing?.program_name} · {removing?.chapter_name}</strong>. El
              contenido no se elimina del banco.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Quitar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
